import { c as _c } from "react/compiler-runtime";
import type { ChainId } from '@morpho-org/blue-sdk';
import type { Address } from 'viem';
import { isDefined } from '@morpho-org/morpho-ts';
import { useCursorPagination } from '@/hooks/useCursorPagination';
import { orpc } from '@/lib/orpc/client';
import { errorCodeOf } from '@/lib/orpc/errors';
import type { VaultTransactionType } from '../config';
import { NO_TRANSACTIONS, toCursorPage, type VaultTransactionsQueryInput, vaultTransactionsQueryKey } from '../vaultTransactionsQuery';
export type VaultTransactionsScope = /** Global activity feed. */
{
  kind: 'activity';
}
/**
 * Viewer-scoped "your transactions" feed. `userAddress: null` means the
 * wallet is disconnected — the query stays disabled.
 */ | {
  kind: 'viewer';
  userAddress: Address | null;
};
interface UseVaultTransactionsTableDataParams<T extends string> {
  address: Address;
  chainId: ChainId;
  vaultVersion: 'v1' | 'v2';
  limit: number;
  filteredTransactionTypes: readonly T[];
  /**
   * Maps each filter enum value to the procedure's `types` value — V1 tables
   * pass `LEGACY_TO_VAULT_TRANSACTION_TYPE`, V2 tables `VAULT_V2_TO_VAULT_TRANSACTION_TYPE`.
   */
  typeMap: Partial<Record<T, VaultTransactionType>>;
  scope: VaultTransactionsScope;
  timestampGte?: number;
  timestampLte?: number;
}

/**
 * The input of one filtered feed, with the unset filters left out so the
 * unfiltered activity feed matches the key the server seeds.
 */
function toQueryInput<T extends string>({
  address,
  chainId,
  vaultVersion,
  limit,
  filteredTransactionTypes,
  typeMap,
  scope,
  timestampGte,
  timestampLte
}: UseVaultTransactionsTableDataParams<T>): VaultTransactionsQueryInput {
  const types: VaultTransactionType[] = [...new Set(filteredTransactionTypes.map(type => typeMap[type]).filter((type): type is VaultTransactionType => isDefined(type)))];
  const userAddress = scope.kind === 'viewer' ? scope.userAddress : null;
  return {
    chainId,
    address,
    vaultVersion,
    limit,
    ...(types.length > 0 && {
      types
    }),
    ...(userAddress !== null && {
      userAddress
    }),
    ...(timestampGte !== undefined && {
      timestampGte
    }),
    ...(timestampLte !== undefined && {
      timestampLte
    })
  };
}

/** Cursor-paginated data hook shared by every vault transaction table. */
export const useVaultTransactionsTableData = params => {
  const $ = _c(13);
  const {
    scope
  } = params;
  let t0;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== params || $[1] !== scope.kind || $[2] !== scope.userAddress) {
    const input = toQueryInput(params);
    t4 = useCursorPagination;
    t0 = vaultTransactionsQueryKey(input, scope.kind);
    t1 = async cursor => {
      ;
      try {
        return toCursorPage(await orpc.vault.transactions.call({
          ...input,
          cursor
        }));
      } catch (t5) {
        const error = t5;
        if (cursor === undefined && errorCodeOf(error) === "NOT_FOUND") {
          return toCursorPage(NO_TRANSACTIONS);
        }
        throw error;
      }
    };
    t2 = scope.kind === "activity" || scope.userAddress !== null;
    t3 = JSON.stringify([input, scope.kind]);
    $[0] = params;
    $[1] = scope.kind;
    $[2] = scope.userAddress;
    $[3] = t0;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t0 = $[3];
    t1 = $[4];
    t2 = $[5];
    t3 = $[6];
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t0 || $[9] !== t1 || $[10] !== t2 || $[11] !== t3) {
    t5 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2,
      resetKey: t3,
      trustCursorOnEmptyPage: true
    };
    $[8] = t0;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t4(t5);
};